import { FC, useEffect, useState } from "react";
import styles from "./Header.module.scss";
import GlobalModals from "./GlobalModals";
import cn from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useWallet } from "@stores/wallet.store";
import { createModalHelpers } from "@stores/unified-modal.store";
import { useModal } from "@hooks/useModal";
import ProfileMenu from "./ProfileSection";

// Import Modal Components with correct paths
import ProfileButton from "@components/ui/Profile/profile-button";

const Header: FC = () => {
    const location = useLocation();
    const { openModal } = createModalHelpers();
    const [handleOpenModal] = useModal(() => openModal("connectWallet"));

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
    const isWalletConnected = useWallet((state) => state.isConnected);

    const handleToggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <>
            <header className="grid max-w-7xl mx-auto md:grid-cols-3 grid-cols-2 items-center justify-center h-12 w-full">
                {/* "grid grid-cols-3 items-center justify-center" */}
                <Link className={styles.logo} to="/">
                    <img width={98} height={22.74} src="/images/header/logo.svg" alt="Nest logo" />
                </Link>
                <nav
                    className={cn(
                        styles.navbar,
                        { [styles.opened]: isMobileMenuOpen },
                        "justify-self-center md:w-auto w-full",
                    )}
                >
                    <ul className={styles.list}>
                        <li className={cn(styles.item, styles.checkers)}>
                            <button type="button">
                                <span>Swaps</span>
                                <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 10 6"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.344988 0.943085C0.560619 0.673546 0.953927 0.629845 1.22347 0.845476L4.9997 3.86646L8.77593 0.845476C9.04547 0.629845 9.43878 0.673546 9.65441 0.943085C9.87004 1.21262 9.82634 1.60593 9.5568 1.82156L5.39013 5.1549C5.16187 5.3375 4.83752 5.3375 4.60926 5.1549L0.442596 1.82156C0.173058 1.60593 0.129357 1.21262 0.344988 0.943085Z"
                                        fill="#5C5C5C"
                                    />
                                </svg>
                            </button>
                            <ul className={styles.menu + " backdrop-blur-sm"}>
                                <li className={styles.menuItem}>
                                    <a className={styles.menuLink} href="/swap">
                                        Swap
                                    </a>
                                </li>
                                <li className={styles.menuItem}>
                                    <a className={styles.menuLink} href="/swap/fast">
                                        Swap Fast
                                    </a>
                                </li>
                                <li className={styles.menuItem}>
                                    <a className={styles.menuLink} href="/memeswap">
                                        MemeSwap
                                    </a>
                                </li>
                            </ul>
                        </li>
                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/sniper">
                                Sniper
                            </NavLink>
                        </li>

                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/limit-orders">
                                <div className="flex w-fit min-w-max">Limit orders</div>
                            </NavLink>
                        </li>

                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/deploy">
                                Deploys
                            </NavLink>
                        </li>

                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/whale">
                                <div className="flex w-fit min-w-max">Whale checker</div>
                            </NavLink>
                        </li>

                        <li className={cn(styles.item, styles.checkers)}>
                            <button type="button">
                                <span>Solutions</span>
                                <svg
                                    width="10"
                                    height="6"
                                    viewBox="0 0 10 6"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.344988 0.943085C0.560619 0.673546 0.953927 0.629845 1.22347 0.845476L4.9997 3.86646L8.77593 0.845476C9.04547 0.629845 9.43878 0.673546 9.65441 0.943085C9.87004 1.21262 9.82634 1.60593 9.5568 1.82156L5.39013 5.1549C5.16187 5.3375 4.83752 5.3375 4.60926 5.1549L0.442596 1.82156C0.173058 1.60593 0.129357 1.21262 0.344988 0.943085Z"
                                        fill="#5C5C5C"
                                    />
                                </svg>
                            </button>
                            <ul className={styles.menu + " backdrop-blur-sm"}>
                                <li className={styles.menuItem}>
                                    <a
                                        className={styles.menuLink}
                                        target="_blank"
                                        href="https://nest-3.gitbook.io/nestdex.io/nest-1"
                                    >
                                        White paper
                                    </a>
                                </li>
                                <li className={styles.menuItem}>
                                    <a
                                        className={styles.menuLink}
                                        target="_blank"
                                        href="https://docs.google.com/document/d/1jiFfF1NblfXCKmp7_CYyU8FlpKTCFVXak55C4U-0SVw/edit?usp=sharing"
                                    >
                                        Privacy policy
                                    </a>
                                </li>
                                <li className={styles.menuItem}>
                                    <a
                                        className={styles.menuLink}
                                        target="_blank"
                                        href="https://docs.google.com/document/d/1vgKMm1pBTGe4PvNiHUkaY3RdC5zzfsjHqm3mk9VFFWs/edit?usp=sharing"
                                    >
                                        Terms of use
                                    </a>
                                </li>
                            </ul>
                        </li>
                        {["/", "/pricing", "/support"].includes(location.pathname) && (
                            <li className={cn(styles.item, styles.disabled)}>
                                <span>Copytrading</span>
                                <span className={styles.badge}>soon</span>
                            </li>
                        )}
                    </ul>
                    <ul className={styles.mobileList}>
                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/swap">
                                Swap
                            </NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink to="/memeswap">Memepad Swap</NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/sniper">
                                Sniper
                            </NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink className={"pointer-events-none opacity-50"} to="/">
                                Limit orders
                            </NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/deploy">
                                Deploys
                            </NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/whale">
                                Whale Checker
                            </NavLink>
                        </li>
                        <li className={styles.item}>
                            <NavLink
                                className={({ isActive }) => (isActive ? styles.active : "")}
                                target="_blank"
                                to="https://nest-3.gitbook.io/nestdex.io/nest-1"
                            >
                                Whitepaper
                            </NavLink>
                        </li>
                        <li>
                            <NavLink className={({ isActive }) => (isActive ? styles.active : "")} to="/support">
                                Support
                            </NavLink>
                        </li>
                    </ul>
                </nav>
                <div className="flex items-center gap-2 justify-self-end">
                    {isWalletConnected ? <ProfileMenu /> : <ProfileButton onClick={handleOpenModal} type="connect" />}
                    <button onClick={handleToggleMobileMenu} className={styles.menuButtonHolder}>
                        <div className={styles.menuButton}></div>
                    </button>
                </div>
            </header>
            {/* Render Modals Globally */}
            <GlobalModals />
        </>
    );
};

export default Header;
